import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import './SearchForm.css';
import { Intake, EXPAND_ALL } from 'new-ui/Components/Intake/Intake';
import HCPSelect from 'new-ui/Components/HCP/Select';
import { GENERAL_DISEASE } from 'utils/constants/disease';
import { useTranslation } from 'utils/modifiedTranslation';
import { useObjectRef } from 'utils/hooks/useObjectRef';
import { useIsMounted } from 'utils/hooks/useIsMounted';
import { QUESTION_TYPE } from 'new-ui/Components/QuestionRenderer/constants';
import SearchIcon from 'assets/icons/search_text.svg';
import UserIcon from 'assets/icons/user3.svg';
import { getUserCondition, getUserConditionProfile } from 'modules/user/utils';
import { getGeneralDiseaseSubType } from 'modules/user/conditionProfileHelpers';
import {
  adjustUser, compareUsers, createIndicationEmptyUser, createMockUser, isUser,
} from 'modules/hcp/helpers';
import { noop } from 'functions/noop';
import { Preloader } from 'components/Preloader';
import { BoxTreatmentsQuestion } from './BoxTreatments';
import { HCP_MOCK } from './constants';
import { getIntakeCustomData, getIntakeQuestions } from './dataProcessing';
import { BoxSelectQuestion } from './components/BoxSelectQuestion';
import { BoxBiomarkersQuestion } from './components/BoxBiomarkersQuestion';
import { BoxLocationQuestion } from './components/BoxLocationQuestion';
import { BoxNumberQuestion } from './components/BoxNumberQuestion';
import { MockQuestion } from './components/MockQuestion';
import {
  fetchIndicationMainOptions,
  fetchIndicationSearchOptions,
  formIndicationMainOptionValue,
  formIndicationOption,
} from './helpers';

let indicationSearchOptions;
let indicationMainOptions;

const QUESTION_RENDERER_BY_QUESTION_TYPE = {
  [QUESTION_TYPE.SINGLE_OPTION_SELECT]: BoxSelectQuestion,
  [QUESTION_TYPE.MULTIPLE_OPTION_SELECT]: BoxSelectQuestion,
  [QUESTION_TYPE.NUMBER_INPUT]: BoxNumberQuestion,
  [QUESTION_TYPE.TREATMENT_HISTORY]: BoxTreatmentsQuestion,
  [QUESTION_TYPE.COMPOSITE]: BoxTreatmentsQuestion,
  [QUESTION_TYPE.BIOMARKERS]: BoxBiomarkersQuestion,
  [QUESTION_TYPE.HCP_TREATMENTS]: BoxTreatmentsQuestion,
  [QUESTION_TYPE.LOCATION]: BoxLocationQuestion,
  [HCP_MOCK]: MockQuestion,
};

const ensureRawIndicationOptions = async () => {
  await Promise.all([
    indicationSearchOptions ? Promise.resolve() : fetchIndicationSearchOptions()
      .then((result) => {
        indicationSearchOptions = result;
      })
      .catch(noop),
    indicationMainOptions ? Promise.resolve() : fetchIndicationMainOptions()
      .then((result) => {
        indicationMainOptions = result;
      })
      .catch(noop),
  ]);
};

const getCustomRenderer = ({ type }) => QUESTION_RENDERER_BY_QUESTION_TYPE[type];

const getSupplementedIndicationOptions = ({ condition, conditionSubType, t }) => {
  if (!condition) {
    return null;
  }

  const mainOptionValue = formIndicationMainOptionValue({ condition, conditionSubType });
  const isMissingMainOption = !(indicationSearchOptions ?? []).find(({ value }) => value === mainOptionValue);

  if (!isMissingMainOption) {
    // GENERAL DISEASE case
    return { options: indicationSearchOptions ?? [], optionValue: mainOptionValue };
  }

  const mainOption = (indicationMainOptions ?? []).find(({ value }) => value === mainOptionValue);

  if (!mainOption) {
    console.error(`cannot find main option for "${condition}" condition "${conditionSubType}" condition sub type`);
    return null;
  }

  const { label, isTranslationNeeded } = mainOption;
  return {
    options: [
      ...(indicationSearchOptions ?? []),
      formIndicationOption({
        label: isTranslationNeeded ? t(label) : label,
        condition,
      }),
    ],
    optionValue: mainOptionValue,
  };
};

const getIndicationSelectorParams = ({ user, t }) => {
  if (!indicationSearchOptions) {
    return { initValue: null, options: [] };
  }

  const userCondition = getUserCondition(user);

  if (!userCondition) {
    return { initValue: null, options: indicationSearchOptions };
  }

  const userConditionSubType = userCondition === GENERAL_DISEASE
    ? getGeneralDiseaseSubType(getUserConditionProfile(user))
    : null;

  const { options, optionValue } = getSupplementedIndicationOptions({
    condition: userCondition, conditionSubType: userConditionSubType, t,
  }) ?? {};

  if (optionValue) {
    return { initValue: optionValue, options };
  }

  // fallback
  // should be invariant
  const initValue = (indicationSearchOptions ?? []).find(({ condition }) => condition === userCondition) ?? null;
  return { initValue, options: indicationSearchOptions ?? [] };
};

const formIntakeKey = ({ user, count }) => {
  let condition = getUserCondition(user);
  if (condition === GENERAL_DISEASE) {
    condition = getGeneralDiseaseSubType(getUserConditionProfile(user));
  }
  return `${condition ?? null}_${count}`;
};

export const SearchForm = ({
  user: userProp, // can be {} to trigger new user creation
  indicationChangeDisabled,
  onChange,
  onLoad,
  onBeforeChange,
  onBeforeRender,
  onAccordionRender,
  id,
}) => {
  const onLoadRef = useRef(onLoad);
  onLoadRef.current = onLoad;

  const isMounted = useIsMounted();

  const [intakeData, setIntakeData] = useState(null);
  const [count, setCount] = useState(0);
  const { t } = useTranslation();

  const { user } = intakeData ?? {};

  const userRef = useObjectRef(user);

  useEffect(() => {
    userRef.current = user ?? null;
  }, [user, userRef]);

  useEffect(() => {
    const { current: currentUser } = userRef;
    if (currentUser && getUserCondition(currentUser) !== getUserCondition(userProp)) {
      // condition mismatch
      setIntakeData(null);
    }

    if (compareUsers(currentUser, userProp)) {
      return;
    }

    // propUser can be {} to trigger new user creation
    let nextUser = isUser(userProp) ? adjustUser(userProp) : null;
    const fetch = async () => {
      await ensureRawIndicationOptions();

      if (!nextUser) {
        nextUser = await createMockUser();
      }

      const intakeQuestions = await getIntakeQuestions(getUserCondition(nextUser));

      if (!isMounted()) {
        return;
      }

      setIntakeData({
        user: nextUser,
        allQuestions: intakeQuestions,
      });
      setCount((prevCount) => prevCount + 1);

      // it is crucial to call onLoad only after calling setIntakeData
      if (onLoadRef.current) {
        onLoadRef.current(nextUser);
      }
    };
    fetch();
  }, [isMounted, userProp, userRef]);

  const handleIndicationChange = (optionValue) => {
    if (indicationChangeDisabled) {
      return;
    }

    const selectedOption = (getSupplementedIndicationOptions({
      condition: getUserCondition(user),
      conditionSubType: getUserCondition(user) === GENERAL_DISEASE
        ? getGeneralDiseaseSubType(getUserConditionProfile(user))
        : null,
      t,
    })?.options ?? indicationSearchOptions ?? [])
      .find((option) => option.value === optionValue);

    if (!selectedOption) {
      return;
    }
    const {
      label,
      condition,
      subType,
      otherQuestionId,
      otherQuestion,
    } = selectedOption;

    if (user && getUserCondition(user) !== condition) {
      // condition mismatch
      setIntakeData(null);
    }

    if (onBeforeChange) {
      onBeforeChange();
    }

    const fetch = async () => {
      if (!condition) {
        const nextUser = await createMockUser();
        const intakeQuestions = await getIntakeQuestions();
        setIntakeData({
          user: nextUser,
          allQuestions: intakeQuestions,
        });
        onChange(nextUser);
        return;
      }

      const { user: { condition_profile: { biomarkers } = {}, info: { country } = {} } = {} } = intakeData;

      const nextUser = await createIndicationEmptyUser(
        condition,
        {
          biomarkers,
          country,
          subType,
          otherQuestionId,
          otherQuestion,
        },
      );

      nextUser.personal.condition_name = label;

      const intakeQuestions = await getIntakeQuestions(getUserCondition(nextUser));

      setIntakeData({
        user: nextUser,
        allQuestions: intakeQuestions,
      });
      onChange(nextUser);
    };

    fetch();
  };

  const { info: { family_name: familyName } = {} } = user ?? {};

  const {
    initValue: indicationSelectorValue,
    options: indicationSelectorOptions,
  } = getIndicationSelectorParams({ user, t });

  return (
    <div className="hcp-form">
      {familyName && (
        <div className="family_name">
          <img src={UserIcon} alt="icon" />
          {familyName}
        </div>
      )}
      {!!indicationSelectorOptions?.length && (
        <div>
          <div className="indication-selector">
            <div className="hcp-input-title">
              {t('general.condition')}
            </div>
            <HCPSelect
              key={indicationSelectorValue}
              disabled={!intakeData || indicationChangeDisabled}
              icon={SearchIcon}
              placeholder={t('hcp.indication_input_title')}
              onselect={handleIndicationChange}
              options={indicationSelectorOptions}
              initValue={indicationSelectorValue}
            />
          </div>
          {!!intakeData && (
            <Intake
              flatMode
              preventNotSure
              shouldRenderDoneButton={false}
              expand={EXPAND_ALL}
              id={`${id}_search`}
              key={formIntakeKey({ user, count })}
              onAccordionRender={onAccordionRender}
              user={user}
              onBeforeRender={onBeforeRender}
              customData={getIntakeCustomData(intakeData)}
              customOnSelect={() => {
                onChange({ ...user });
              }}
              getCustomRenderer={getCustomRenderer}
            />
          )}
        </div>
      )}
      {(!indicationSelectorOptions?.length || !intakeData) && (
        <div className="intake-preloader">
          <Preloader inline isVisible />
        </div>
      )}
    </div>
  );
};

SearchForm.propTypes = {
  id: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  user: PropTypes.object,
  onLoad: PropTypes.func,
  onBeforeChange: PropTypes.func,
  onBeforeRender: PropTypes.func,
  onAccordionRender: PropTypes.func,
  indicationChangeDisabled: PropTypes.bool,
};
